export const LOGIN_VERIFICATION_METHODS = Object.freeze({
  CUSTOMIZED: 'customized',
  EMAIL: 'email',
});

export const EMAIL_OTP_LIFETIME_SECONDS = 5 * 60;

const normalizeLoginIdentity = (email, accountId) => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedAccountId = String(accountId || '').trim();

  if (!normalizedEmail || !normalizedAccountId) {
    throw new Error('LOGIN_IDENTITY_UNAVAILABLE');
  }

  return { normalizedEmail, normalizedAccountId };
};

export const buildLoginChallengeRequest = ({ email, accountId, role }) => {
  const { normalizedEmail, normalizedAccountId } = normalizeLoginIdentity(email, accountId);
  const normalizedRole = String(role || '').trim();

  if (!normalizedRole) {
    throw new Error('LOGIN_ROLE_UNAVAILABLE');
  }

  return {
    email: normalizedEmail,
    account_id: normalizedAccountId,
    companyNumber: normalizedAccountId,
    role: normalizedRole,
  };
};

export const buildLoginVerificationRequest = ({
  email,
  accountId,
  authenticationKey,
  verificationMethod,
}) => {
  const { normalizedEmail, normalizedAccountId } = normalizeLoginIdentity(email, accountId);

  if (!Object.values(LOGIN_VERIFICATION_METHODS).includes(verificationMethod)) {
    throw new Error('INVALID_VERIFICATION_METHOD');
  }

  return {
    authentication_key: String(authenticationKey || '').trim(),
    email: normalizedEmail,
    account_id: normalizedAccountId,
    companyNumber: normalizedAccountId,
    verification_method: verificationMethod,
  };
};

export const parseLoginVerificationChallenge = (payload) => {
  if (!payload || Array.isArray(payload) || typeof payload !== 'object') {
    throw new Error('INVALID_VERIFICATION_CHALLENGE');
  }

  const { status, verification_method: verificationMethod, expires_in_seconds: expiresInSeconds } = payload;

  if (status !== 'success') {
    throw new Error('VERIFICATION_CHALLENGE_REJECTED');
  }

  if (verificationMethod === LOGIN_VERIFICATION_METHODS.CUSTOMIZED) {
    if (expiresInSeconds !== null) {
      throw new Error('INVALID_CUSTOMIZED_OTP_EXPIRY');
    }

    return {
      verificationMethod,
      expiresInSeconds: null,
    };
  }

  if (
    verificationMethod === LOGIN_VERIFICATION_METHODS.EMAIL
    && expiresInSeconds === EMAIL_OTP_LIFETIME_SECONDS
  ) {
    return {
      verificationMethod,
      expiresInSeconds,
    };
  }

  throw new Error('INVALID_VERIFICATION_METHOD');
};

export const parsePasswordResetChallenge = (payload) => {
  // Temporary compatibility while the frontend and backend are deployed.
  if (payload === 'success') {
    return { expiresInSeconds: EMAIL_OTP_LIFETIME_SECONDS };
  }

  if (
    payload
    && !Array.isArray(payload)
    && typeof payload === 'object'
    && payload.status === 'success'
    && payload.expires_in_seconds === EMAIL_OTP_LIFETIME_SECONDS
  ) {
    return { expiresInSeconds: payload.expires_in_seconds };
  }

  throw new Error('INVALID_PASSWORD_RESET_CHALLENGE');
};

export const isLoginVerificationMatched = (payload, expectedMethod) => {
  // Temporary compatibility for a rolling frontend/backend deployment.
  if (payload === 'Matched') {
    return true;
  }

  return Boolean(
    payload
    && !Array.isArray(payload)
    && typeof payload === 'object'
    && payload.status === 'success'
    && payload.matched === true
    && payload.verification_method === expectedMethod
  );
};

export const getEmailOtpSecondsRemaining = (issuedAt, now = Date.now()) => {
  if (!Number.isFinite(issuedAt)) {
    return 0;
  }

  const expiresAt = issuedAt + (EMAIL_OTP_LIFETIME_SECONDS * 1000);
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
};

export const isEmailOtpExpired = (issuedAt, now = Date.now()) => (
  getEmailOtpSecondsRemaining(issuedAt, now) === 0
);

export const formatEmailOtpTimeRemaining = (seconds) => {
  const normalizedSeconds = Number.isFinite(seconds)
    ? Math.max(0, Math.ceil(seconds))
    : EMAIL_OTP_LIFETIME_SECONDS;
  const minutes = Math.floor(normalizedSeconds / 60);
  const remainingSeconds = normalizedSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};
